import React from 'react';
import { 
  HomeIcon,
  UserGroupIcon,
  ArrowPathIcon,
  TrophyIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

interface NavigationProps {
  currentPage: string;
  onNavigate?: (page: string) => void;
}

interface NavItem {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  description: string;
  badge?: string | number;
}

const Navigation: React.FC<NavigationProps> = ({ currentPage, onNavigate }) => {
  const navItems: NavItem[] = [
    {
      id: 'dashboard',
      name: '仪表盘',
      icon: HomeIcon,
      description: '系统概览和快速操作'
    },
    {
      id: 'queue',
      name: '队列管理',
      icon: ArrowPathIcon,
      description: '查看和管理轮换队列'
    },
    {
      id: 'players',
      name: '人员管理',
      icon: UserGroupIcon,
      description: '添加、移除和管理玩家'
    },
    {
      id: 'match',
      name: '比赛管理',
      icon: PlayIcon,
      description: '配对生成和比赛进行'
    },
    {
      id: 'history',
      name: '比赛历史',
      icon: TrophyIcon,
      description: '查看历史比赛记录'
    },
    {
      id: 'statistics',
      name: '数据统计',
      icon: ChartBarIcon,
      description: '个人和整体统计分析'
    },
    {
      id: 'settings',
      name: '系统设置',
      icon: Cog6ToothIcon,
      description: '系统配置和偏好设置'
    }
  ];

  const handleNavClick = (itemId: string) => {
    onNavigate?.(itemId);
  };

  return (
    <nav className="px-3 space-y-1">
      {/* Navigation header */}
      <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        主要功能
      </div>

      {/* Navigation items */}
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = currentPage === item.id;
        
        return (
          <button
            key={item.id}
            onClick={() => handleNavClick(item.id)}
            className={`w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              isActive
                ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Icon
              className={`mr-3 flex-shrink-0 h-5 w-5 ${
                isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
              }`}
            />
            <div className="flex-1 text-left">
              <div className="flex items-center justify-between">
                <span>{item.name}</span>
                {item.badge && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {item.badge}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5 hidden lg:block">
                {item.description}
              </div>
            </div>
          </button>
        );
      })}

      {/* Quick actions section */}
      <div className="pt-6">
        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          快速操作
        </div>
        
        <div className="space-y-1">
          <QuickActionButton
            icon={PlayIcon}
            label="开始比赛"
            description="快速开始新比赛"
            color="bg-green-50 text-green-700 hover:bg-green-100"
            onClick={() => handleNavClick('quick-match')}
          />
          
          <QuickActionButton
            icon={ArrowPathIcon}
            label="轮换队列"
            description="执行队列轮换"
            color="bg-blue-50 text-blue-700 hover:bg-blue-100"
            onClick={() => handleNavClick('quick-rotate')}
          />
          
          <QuickActionButton
            icon={UserGroupIcon}
            label="添加玩家"
            description="快速添加新玩家"
            color="bg-purple-50 text-purple-700 hover:bg-purple-100"
            onClick={() => handleNavClick('quick-add-player')}
          />
        </div>
      </div>

      {/* System info */}
      <div className="pt-6 border-t border-gray-200">
        <div className="px-3 py-2">
          <div className="text-xs text-gray-500 space-y-1">
            <div>版本: v1.0.0</div>
            <div>© 2024 轮换比赛系统</div>
          </div>
        </div>
      </div>
    </nav>
  );
};

interface QuickActionButtonProps {
  icon: React.ComponentType<any>;
  label: string;
  description: string;
  color: string;
  onClick: () => void;
}

const QuickActionButton: React.FC<QuickActionButtonProps> = ({
  icon: Icon,
  label,
  description,
  color,
  onClick
}) => {
  return (
    <button
      onClick={onClick}
      className={`w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${color}`}
    >
      <Icon className="mr-3 flex-shrink-0 h-4 w-4" />
      <div className="flex-1 text-left">
        <div>{label}</div>
        <div className="text-xs opacity-75 hidden lg:block">
          {description}
        </div>
      </div>
    </button>
  );
};

export default Navigation;